import { Stack } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useEffect, useState } from "react";
import { borderStyle, defaultGap } from "../../../styles/consts";
import { getItemsFromIndex, getNestedProperty } from "../../../utils";
import { Option } from "../fundamentals/Dropdown";
import Loader from "../fundamentals/Loader/Loader";
import { FilterValue } from "./ColumnFilter";
import FilteringArea from "./FilteringArea";
import LabelRow from "./LabelRow";
import PageNavigation from "./PageNavigation";
import Row, { ItemRow } from "./Row";

export type Sort = {
  param: string;
  order: "asc" | "desc";
};

interface Match {
  status?: string;
  [key: string]: any;
}

interface Offer {
  matches?: Match[];
  [key: string]: any;
}

export default function Table<T extends Offer>({
  items,
  filterFunction,
  getItemRow,
  filters,
  idProperty,
  deleteAllFunction,
  defaultSort,
  deletable = true,
  filteringArea,
  saveSortName,
  saveFilterName,
  saveActiveFiltersName,
  savePageNum,
  loading = false,
  visibleRowsNo = 7,
  additionalOnCheckboxClick,
  callOnNextPage,
  hideDropdown = false,
  upperStateForPage,
  setUpperStateForPage,
  onColumnFilterChange,
}: {
  items: T[];
  filterFunction?: (value: string) => T[];
  getItemRow: (item: T, additionalFilter?: string) => ItemRow[];
  filters?: Option[];
  idProperty: string;
  deleteAllFunction?: (ids: string[]) => void;
  defaultSort?: Sort;
  deletable?: boolean;
  filteringArea?: JSX.Element;
  saveSortName?: string;
  saveFilterName?: string;
  saveActiveFiltersName?: string;
  savePageNum?: string;
  loading?: boolean;
  visibleRowsNo?: number;
  additionalOnCheckboxClick?: (item: T) => void;
  callOnNextPage?: () => void;
  hideDropdown?: boolean;
  upperStateForPage?: number;
  setUpperStateForPage?: (page: number) => void;
  onColumnFilterChange?: (param: string, value: FilterValue | null) => void;
}) {
  const savedPage = savePageNum ? localStorage.getItem(savePageNum) : null;
  const [page, setPage] = useState(savedPage ? parseInt(savedPage) : 1);

  const currentPage =
    upperStateForPage !== undefined ? upperStateForPage : page;

  const currentSetPage = setUpperStateForPage || setPage;

  const [filteredItems, setFilteredItems] = useState<T[]>(items);
  const [currentlyDisplayedItems, setCurrentlyDisplayedItems] = useState<T[]>(
    [],
  );
  const [selectedItemIds, setselectedItemIds] = useState<string[]>([]);
  const [currentSort, setCurrentSort] = useState<Sort>(
    defaultSort || {
      param: idProperty,
      order: "desc",
    },
  );
  const [activeFilters, setActiveFilters] = useState<
    Record<string, FilterValue>
  >(() => {
    if (!saveActiveFiltersName) return {};
    try {
      const raw = localStorage.getItem(saveActiveFiltersName);
      return raw
        ? JSON.parse(raw, (_key, val) => {
            if (
              typeof val === "string" &&
              /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z$/.test(val)
            ) {
              return new Date(val);
            }
            return val;
          })
        : {};
    } catch (e) {
      console.warn(
        `Table: failed to parse stored activeFilters (${saveActiveFiltersName})`,
        e,
      );
      return {};
    }
  });

  const labels = getItemRow({} as T);

  const handleCheckboxClick = (item: T) => {
    const itemId = item[idProperty];
    setselectedItemIds((prev) => {
      if (selectedItemIds.includes(itemId)) {
        return prev.filter((id) => id !== itemId);
      } else {
        return [...prev, itemId];
      }
    });
    additionalOnCheckboxClick && additionalOnCheckboxClick(item);
  };

  const changeSort = (param?: string) => {
    if (!param) return;

    setCurrentSort((prev): Sort => {
      const sort: Sort = {
        param,
        order: prev.param === param && prev.order === "asc" ? "desc" : "asc",
      };
      if (saveSortName) {
        localStorage.setItem(saveSortName, JSON.stringify(sort));
      }
      return sort;
    });
  };

  const handleFilterChange = (param: string, value: FilterValue | null) => {
    setActiveFilters((prev) => {
      let next: Record<string, FilterValue>;
      if (value === null) {
        const { [param]: _, ...rest } = prev;
        next = rest;
      } else {
        next = { ...prev, [param]: value };
      }
      if (saveActiveFiltersName) {
        try {
          localStorage.setItem(saveActiveFiltersName, JSON.stringify(next));
        } catch (e) {
          console.warn(
            `Table: failed to persist activeFilters (${saveActiveFiltersName})`,
            e,
          );
        }
      }
      return next;
    });
    currentSetPage(1);
    if (onColumnFilterChange) {
      onColumnFilterChange(param, value);
    }
  };

  const applyFilters = (items: T[]) => {
    return items.filter((item) => {
      return Object.entries(activeFilters).every(([param, filter]) => {
        if (filter.value === null) return true;

        if (param === "status") {
          if (!item.matches || !Array.isArray(item.matches)) return false;
          return item.matches.some((match) => {
            // "sent", "rejected", "timed_out" are terminal statuses that should not be overridden
            let effectiveStatus = match.status;
            if (
              match.status !== "sent" &&
              match.status !== "rejected" &&
              match.status !== "timed_out"
            ) {
              if (match.supervisor_confirmed) {
                effectiveStatus = "approved";
              } else if (match.consultant_confirmed) {
                effectiveStatus =
                  match.supervisor_enabled_at_confirmation === false
                    ? "approved"
                    : "supervisor_review";
              }
            }
            if (effectiveStatus === "viewed") {
              effectiveStatus = "consultant_review";
            }

            if (
              filter.value === "consultant_review" &&
              match.status === "viewed"
            ) {
              return true;
            }

            return effectiveStatus === filter.value;
          });
        }

        if (param === "confirmation_status") {
          if (!item.matches || !Array.isArray(item.matches)) return false;
          const filterValue = filter.value as string;

          return item.matches.some((match) => {
            if (filterValue === "both") {
              return match.consultant_confirmed && match.supervisor_confirmed;
            } else if (filterValue === "candidate") {
              return match.consultant_confirmed;
            } else if (filterValue === "supervisor") {
              return match.supervisor_confirmed;
            } else if (filterValue === "none") {
              return !match.consultant_confirmed && !match.supervisor_confirmed;
            }
            return false;
          });
        }

        if (param === "matches.score") {
          if (!item.matches || !Array.isArray(item.matches)) return false;
          const range = filter.value as {
            min: number | null;
            max: number | null;
          };

          return item.matches.some((match) => {
            const score = match.score;
            if (score === undefined || score === null) return false;

            const minValid = range.min === null || score >= range.min;
            const maxValid = range.max === null || score <= range.max;

            return minValid && maxValid;
          });
        }
        const value = getNestedProperty(item, param);

        if (value === undefined || value === null) return false;

        switch (filter.type) {
          case "text":
            const filterValue = String(filter.value).toLowerCase();
            return filterValue === ""
              ? true
              : String(value).toLowerCase().includes(filterValue);
          case "date":
            const dateRange = filter.value as {
              min: Date | null;
              max: Date | null;
            };
            const itemDate = new Date(value);

            const minValid =
              dateRange.min === null || itemDate >= dateRange.min;
            const maxValid =
              dateRange.max === null || itemDate <= dateRange.max;

            return minValid && maxValid;
          case "select":
            return value === filter.value;
          case "number":
            return Number(value) === Number(filter.value);
          default:
            return true;
        }
      });
    });
  };

  useEffect(() => {
    const getSortedItems = (items: T[]) => {
      return items.sort((a, b) => {
        let aVal = getNestedProperty(a, currentSort.param);
        let bVal = getNestedProperty(b, currentSort.param);

        if (currentSort.param === "confirmation_status") {
          const getConfirmationScore = (item: any) => {
            if (!item.matches || item.matches.length === 0) return -1;
            return Math.max(
              ...item.matches.map((match: any) => {
                return (
                  (match.consultant_confirmed ? 1 : 0) +
                  (match.supervisor_confirmed ? 1 : 0)
                );
              }),
            );
          };

          const aScore = getConfirmationScore(a);
          const bScore = getConfirmationScore(b);

          if (currentSort.order === "desc") {
            return bScore - aScore;
          } else {
            return aScore - bScore;
          }
        }

        if (currentSort.param === "status") {
          const statusPriority: Record<string, number> = {
            approved: 7,
            supervisor_review: 6,
            sent: 5,
            consultant_review: 4,
            viewed: 4, // Same priority as consultant_review
            pending: 3,
            rejected: 2,
            timed_out: 1,
          };

          const getStatusPriority = (item: any) => {
            if (!item.matches || item.matches.length === 0) {
              return statusPriority["pending"] || 0;
            }

            return Math.max(
              ...item.matches.map((match: any) => {
                // "sent", "rejected", "timed_out" are terminal statuses that should not be overridden
                let effectiveStatus = match.status;
                if (
                  match.status !== "sent" &&
                  match.status !== "rejected" &&
                  match.status !== "timed_out"
                ) {
                  if (match.supervisor_confirmed) {
                    effectiveStatus = "approved";
                  } else if (match.consultant_confirmed) {
                    effectiveStatus =
                      match.supervisor_enabled_at_confirmation === false
                        ? "approved"
                        : "supervisor_review";
                  }
                }
                // Handle legacy "viewed" status
                if (effectiveStatus === "viewed") {
                  effectiveStatus = "consultant_review";
                }
                return statusPriority[effectiveStatus] || 0;
              }),
            );
          };

          const aPriority = getStatusPriority(a);
          const bPriority = getStatusPriority(b);

          if (currentSort.order === "desc") {
            return bPriority - aPriority;
          } else {
            return aPriority - bPriority;
          }
        }

        if (aVal === undefined && bVal !== undefined) {
          return currentSort.order === "asc" ? -1 : 1;
        } else if (aVal !== undefined && bVal === undefined) {
          return currentSort.order === "asc" ? 1 : -1;
        } else if (aVal === undefined && bVal === undefined) {
          return 0;
        }

        if (currentSort.order === "asc") {
          return aVal > bVal ? 1 : -1;
        } else {
          return aVal < bVal ? 1 : -1;
        }
      });
    };

    const filtered = applyFilters(filteredItems);
    setCurrentlyDisplayedItems(
      getItemsFromIndex(
        getSortedItems(filtered),
        (currentPage - 1) * visibleRowsNo,
        visibleRowsNo,
      ),
    );
  }, [filteredItems, currentSort, currentPage, activeFilters]);

  const savedFilter = saveFilterName
    ? localStorage.getItem(saveFilterName)
    : null;

  useEffect(() => {
    let filteredItems = items;
    if (savedFilter && filterFunction) {
      filteredItems = filterFunction(savedFilter);
    }
    setFilteredItems(filteredItems);
  }, [items, filterFunction, saveFilterName]);

  const handleAllDisplayedSelect = () => {
    if (selectedItemIds.length > 0) {
      setselectedItemIds([]);
    } else {
      setselectedItemIds(
        currentlyDisplayedItems.map((item) => item[idProperty]),
      );
    }
  };

  const filteredItemsCount = applyFilters(filteredItems).length;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Stack
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Stack
          sx={{
            flex: "1 1 auto",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Stack
            sx={{
              padding: `${defaultGap} 0 0.75rem ${defaultGap}`,
              borderBottom: borderStyle,
              boxSizing: "border-box",
              flexShrink: 0,
            }}
          >
            {filterFunction && filters && (
              <FilteringArea
                deletable={deletable}
                deleteAllFunction={() => {
                  if (deleteAllFunction) {
                    deleteAllFunction(selectedItemIds);
                  }
                }}
                filterFunction={filterFunction}
                setFilteredItems={setFilteredItems}
                setPage={currentSetPage}
                filters={filters}
                anySelected={selectedItemIds.length > 0}
                items={items}
                filteringArea={filteringArea}
                saveFilterName={saveFilterName}
                hideDropdown={hideDropdown}
              />
            )}

            <LabelRow
              labels={labels}
              isChecked={selectedItemIds.length > 0}
              handleAllDisplayedSelect={handleAllDisplayedSelect}
              currentSort={currentSort}
              changeSort={changeSort}
              onFilterChange={handleFilterChange}
              activeFilters={activeFilters}
            />
          </Stack>
          <Stack
            sx={{
              width: "100%",
              flex: "1 1 auto",
              overflow: "auto",
              position: "relative",
              display: "flex",
              flexDirection: "column",
              alignItems: loading ? "center" : "stretch",
              justifyContent: loading ? "center" : "flex-start",
              minHeight: 0,
            }}
          >
            {loading ? (
              <Loader />
            ) : (
              currentlyDisplayedItems.map((item, idx) => (
                <Row
                  key={idx}
                  itemRow={
                    savedFilter
                      ? getItemRow(item, savedFilter)
                      : getItemRow(item)
                  }
                  handleCheckboxClick={() => handleCheckboxClick(item)}
                  isChecked={selectedItemIds.includes(item[idProperty])}
                />
              ))
            )}
          </Stack>
        </Stack>
        <PageNavigation
          page={currentPage}
          setPage={currentSetPage}
          filteredItems={filteredItemsCount}
          visibleRowsNo={visibleRowsNo}
          callOnNextPage={callOnNextPage}
          storageKey={savePageNum}
        />
      </Stack>
    </LocalizationProvider>
  );
}
